
import { Link } from 'react-router-dom';
import BlogHeader from './components/BlogHeader';
import Footer from '../home/components/Footer';
import NewsletterSignup from './components/NewsletterSignup';
import { useEffect } from 'react';

const blogPosts = [
  {
    slug: '/blog/top-cloud-accounting-softwares',
    title: 'Top 7 Cloud-Based Accounting Softwares for Small Business',
    excerpt:
      'Choosing the right cloud-based accounting software can transform how you manage your finances. We break down the seven best platforms so you can find the perfect fit for your El Paso business.',
    image:
      'https://public.readdy.ai/ai/img_res/9c9a8c5f68cfbf785d4c60919ffc3bcb.jpg',
    imageAlt: 'Cloud-based accounting software comparison guide for El Paso small businesses',
    category: 'Accounting Software',
    date: 'January 15, 2025',
    readTime: '12 min read',
  },
  {
    slug: '/blog/bookkeeping-mistakes-el-paso',
    title: '5 Bookkeeping Mistakes El Paso Business Owners Make',
    excerpt:
      'Avoid these costly bookkeeping errors that could be hurting your bottom line. Learn how to protect your El Paso business from common financial pitfalls and keep your books accurate.',
    image:
      'https://readdy.ai/api/search-image?query=Stressed%20business%20owner%20at%20desk%20surrounded%20by%20messy%20paperwork%20receipts%20and%20invoices%20with%20calculator%20and%20laptop%20showing%20financial%20spreadsheet%2C%20warm%20office%20lighting%20with%20plants%2C%20El%20Paso%20Texas%20business%20setting%2C%20professional%20photography%20with%20shallow%20depth%20of%20field&width=800&height=450&seq=bookkeeping-mistakes-hero-001&orientation=landscape',
    imageAlt: '5 common bookkeeping mistakes El Paso TX business owners make',
    category: 'Business Tips',
    date: 'January 20, 2025',
    readTime: '10 min read',
  },
  {
    slug: '/compare-el-paso-tax-preparation-services',
    title: 'How to Compare Tax Preparation Services in El Paso TX: The Complete Guide',
    excerpt:
      'Not all tax preparers are created equal. Learn what credentials to look for, what questions to ask, red flags to avoid, and how to find the right tax preparation service for your El Paso business.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20tax%20preparation%20office%20in%20El%20Paso%20Texas%20with%20accountant%20reviewing%20financial%20documents%20and%20tax%20forms%20on%20a%20clean%20modern%20desk%2C%20warm%20natural%20lighting%20through%20large%20windows%20with%20desert%20mountain%20view%2C%20organized%20workspace%20with%20laptop%20showing%20tax%20software%2C%20professional%20business%20setting%20with%20teal%20and%20warm%20earth%20tone%20accents&width=800&height=450&seq=tax-prep-compare-hero-ep-001&orientation=landscape',
    imageAlt: 'How to compare tax preparation services in El Paso TX guide',
    category: 'Tax Preparation',
    date: 'February 3, 2025',
    readTime: '14 min read',
  },
  {
    slug: '/blog/why-el-paso-small-businesses-need-monthly-bookkeeping',
    title: 'Why El Paso Small Businesses Need Monthly Bookkeeping',
    excerpt:
      'Discover how consistent monthly bookkeeping protects your El Paso TX business from costly surprises, keeps you tax-ready year-round, and fuels smarter growth decisions with seven compelling reasons.',
    image:
      'https://readdy.ai/api/search-image?query=Professional%20female%20bookkeeper%20working%20on%20laptop%20with%20financial%20spreadsheets%20and%20charts%20in%20a%20bright%20modern%20El%20Paso%20Texas%20office%2C%20organized%20desk%20with%20coffee%20mug%20and%20green%20plant%2C%20warm%20golden%20hour%20sunlight%20streaming%20through%20window%20with%20desert%20mountain%20view%2C%20clean%20minimalist%20workspace%20with%20teal%20accent%20decor&width=800&height=450&seq=monthly-bk-hero-ep-001&orientation=landscape',
    imageAlt: 'Why El Paso TX small businesses need monthly bookkeeping services',
    category: 'Monthly Bookkeeping',
    date: 'February 18, 2025',
    readTime: '14 min read',
  },
];

export default function BlogIndexPage() {
  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  // Defensive guard – if the data array somehow becomes empty, show a friendly message.
  if (!Array.isArray(blogPosts) || blogPosts.length === 0) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-8 p-4">
        <p className="text-gray-500">No blog posts available at the moment.</p>
        <BlogHeader />
        <NewsletterSignup />
        <Footer />
      </div>
    );
  }

  const featured = blogPosts[0];

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasoquickbooksbookkeeping.com/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://elpasoquickbooksbookkeeping.com/blog",
      },
    ],
  };

  const blogData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "El Paso Bookkeeping Blog - QuickBooks Tips & Financial Insights",
    "description":
      "Expert bookkeeping insights, accounting software reviews, tax preparation guides, and financial management tips for El Paso TX businesses.",
    "url": "https://elpasoquickbooksbookkeeping.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "El Paso QuickBooks Bookkeeping Services",
    },
    "blogPost": blogPosts.map((post) => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "url": `https://elpasoquickbooksbookkeeping.com${post.slug}`,
    })),
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <BlogHeader />

      {/* Hero */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-50">
        <div className="max-w-5xl mx-auto px-6 pt-16 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight font-display">
            El Paso Bookkeeping Insights &amp; Resources
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Expert advice on{' '}
            <strong>accounting software</strong>,{' '}
            <strong>monthly bookkeeping</strong>, tax preparation, and business growth
            strategies for <strong>El Paso TX</strong> business owners.
          </p>
        </div>
      </section>

      <main>
        {/* Featured Post */}
        <section className="max-w-6xl mx-auto px-6 -mt-8 mb-16 relative z-10">
          <Link
            to={featured.slug}
            className="group block bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 hover:shadow-2xl transition-shadow cursor-pointer"
          >
            <div className="grid md:grid-cols-2">
              <div className="w-full h-64 md:h-auto">
                <img
                  src={featured.image}
                  alt={featured.imageAlt}
                  title={featured.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-3 py-1 rounded-full mb-4 w-fit">
                  Featured
                </span>
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4 group-hover:text-teal-600 transition-colors leading-snug">
                  {featured.title}
                </h2>
                <p className="text-gray-600 text-base leading-relaxed mb-6">{featured.excerpt}</p>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs">
                    {featured.category}
                  </span>
                  <span>{featured.date}</span>
                  <span>{featured.readTime}</span>
                </div>
              </div>
            </div>
          </Link>
        </section>

        {/* All Posts Grid */}
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <h4 className="text-2xl font-bold text-gray-900 mb-8">
            <a href="#all-posts" className="hover:text-teal-600 transition-colors">
              All Articles
            </a>
          </h4>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <Link
                key={post.slug}
                to={post.slug}
                className="group bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-all overflow-hidden cursor-pointer"
              >
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.imageAlt}
                    title={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold text-teal-600 mb-2 block">
                    {post.category}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors leading-snug line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-gray-400 border-t border-gray-100 pt-4">
                    <span>{post.date}</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Recommended Reading Cross-Links */}
        <section className="max-w-6xl mx-auto px-6 pb-20">
          <div className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-2xl p-8 border border-gray-100">
            <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">
              <a href="#recommended" className="hover:text-teal-600 transition-colors">
                Popular Reads From Our Team
              </a>
            </h4>
            <div className="grid md:grid-cols-3 gap-6">
              <Link
                to="/blog/top-cloud-accounting-softwares"
                className="flex items-start gap-3 bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-cloud-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors leading-snug">
                    Find the ideal accounting software for your growing business
                  </p>
                  <span className="text-xs text-gray-400 mt-1 block">12 min read</span>
                </div>
              </Link>
              <Link
                to="/blog/bookkeeping-mistakes-el-paso"
                className="flex items-start gap-3 bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line text-orange-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors leading-snug">
                    Stop making these expensive bookkeeping blunders
                  </p>
                  <span className="text-xs text-gray-400 mt-1 block">10 min read</span>
                </div>
              </Link>
              <Link
                to="/compare-el-paso-tax-preparation-services"
                className="flex items-start gap-3 bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-search-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-teal-600 transition-colors leading-snug">
                    What to look for when hiring a tax preparer in El Paso
                  </p>
                  <span className="text-xs text-gray-400 mt-1 block">14 min read</span>
                </div>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <NewsletterSignup />
      <Footer />

      {/* Structured data – must use a non‑self‑closing <script> element */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      ></script>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(blogData) }}
      ></script>
    </div>
  );
}
